import { useEffect, useState } from "react";
import { api, formatErr, API, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle, Wrench, Calendar as CalIcon, Phone, ArrowRight, Plus, ChevronLeft, ChevronRight, List, LayoutGrid, Pencil, Trash2, FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { PaginationBar, usePagination } from "@/components/Pagination";

const statusColor = {
  Pending: "text-yellow-500 bg-yellow-500/10 border-yellow-500/40",
  Confirmed: "text-blue-400 bg-blue-500/10 border-blue-500/40",
  "In Progress": "text-orange-400 bg-orange-500/10 border-orange-500/40",
  Completed: "text-green-500 bg-green-500/10 border-green-500/40",
  Cancelled: "text-red-500 bg-red-500/10 border-red-500/40",
};
const rawStatusColor = {
  requested: statusColor.Pending,
  confirmed: statusColor.Confirmed,
  converted: statusColor["In Progress"],
  cancelled: statusColor.Cancelled,
};

const emptyForm = { customer_id: "", vehicle_id: "", job_type: "", package_id: "", mechanic_id: "", odometer: "", slot_date: "", slot_time: "10:00", notes: "" };
const emptyCust = { name: "", phone: "", address: "" };
const emptyVeh = { make: "", model: "", registration: "", year: "" };
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function Bookings() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [packages, setPackages] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [jobCardTypes, setJobCardTypes] = useState([]);
  const [view, setView] = useState("list"); // list | calendar
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [monthCursor, setMonthCursor] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(emptyForm);
  const [custOpen, setCustOpen] = useState(false);
  const [newCust, setNewCust] = useState(emptyCust);
  const [vehOpen, setVehOpen] = useState(false);
  const [newVeh, setNewVeh] = useState(emptyVeh);
  const [vehHist, setVehHist] = useState([]);

  const canManage = ["owner", "manager", "receptionist"].includes(user.role);
  const canDelete = ["owner", "manager"].includes(user.role);

  const load = () => api.get("/bookings").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
    api.get("/packages").then(r => setPackages(r.data)).catch(() => {});
    api.get("/users").then(r => setMechanics(r.data.filter(u => u.role === "mechanic"))).catch(() => {});
    api.get("/job-card-types").then(r => setJobCardTypes(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (f.customer_id) api.get(`/vehicles?customer_id=${f.customer_id}`).then(r => setVehicles(r.data));
    else setVehicles([]);
  }, [f.customer_id]);
  useEffect(() => {
    if (open && f.vehicle_id) api.get(`/vehicles/${f.vehicle_id}/detail`).then(r => setVehHist(r.data.service_history || [])).catch(() => setVehHist([]));
    else setVehHist([]);
  }, [f.vehicle_id, open]);

  const filtered = list.filter(b =>
    (!dateFrom || b.slot_date >= dateFrom) && (!dateTo || b.slot_date <= dateTo)
  );
  const pg = usePagination(filtered);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}/status?status=${status}`);
      if (data.job_id) toast.success(`Confirmed & job ${data.job_number} auto-created`);
      else toast.success(status);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (b) => {
    setEditId(b.id);
    setF({
      customer_id: b.customer_id || "", vehicle_id: b.vehicle_id || "",
      job_type: b.job_type || b.service_type || "", package_id: b.package_id || "",
      mechanic_id: b.mechanic_id || "", odometer: b.odometer ?? "",
      slot_date: b.slot_date || "", slot_time: b.slot_time || "10:00",
      notes: b.notes || "",
    });
    setOpen(true);
  };

  const del = async (b) => {
    if (!confirm(`Delete booking ${b.booking_number}? This cannot be undone.`)) return;
    try { await api.delete(`/bookings/${b.id}`); toast.success("Booking deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const submit = async () => {
    if (!f.customer_id || !f.slot_date || !f.slot_time) { toast.error("Customer, date & time required"); return; }
    const payload = {
      customer_id: f.customer_id, vehicle_id: f.vehicle_id || null,
      package_id: f.package_id || null, job_type: f.job_type || null,
      mechanic_id: f.mechanic_id || null,
      odometer: f.odometer !== "" ? Number(f.odometer) : null,
      slot_date: f.slot_date, slot_time: f.slot_time,
      notes: f.notes || null,
    };
    try {
      if (editId) await api.patch(`/bookings/${editId}`, payload);
      else await api.post("/bookings", payload);
      toast.success(editId ? "Booking updated" : "Booking created");
      setF(emptyForm); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveCust = async () => {
    if (!newCust.name || !newCust.phone) { toast.error("Name & phone required"); return; }
    try {
      const { data } = await api.post("/customers", { name: newCust.name, phone: newCust.phone, address: newCust.address || null, email: null });
      await api.get("/customers").then(r => setCustomers(r.data));
      setF(x => ({ ...x, customer_id: data.id, vehicle_id: "" }));
      setNewCust(emptyCust); setCustOpen(false);
      toast.success("Customer added");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveVeh = async () => {
    if (!newVeh.make || !newVeh.model || !newVeh.registration) { toast.error("Make, model & registration required"); return; }
    try {
      const { data } = await api.post("/vehicles", {
        customer_id: f.customer_id, make: newVeh.make, model: newVeh.model,
        registration: newVeh.registration.toUpperCase(),
        year: newVeh.year ? Number(newVeh.year) : null,
      });
      const { data: vs } = await api.get(`/vehicles?customer_id=${f.customer_id}`);
      setVehicles(vs);
      setF(x => ({ ...x, vehicle_id: data.id }));
      setNewVeh(emptyVeh); setVehOpen(false);
      toast.success("Vehicle added & selected");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openJob = async (booking) => {
    if (booking.job_id) nav(`/jobcards?open=${booking.job_id}`);
    else toast.info("This booking hasn't been converted to a job card yet");
  };

  const openOrCreateJob = async (b) => {
    if (b.job_id) { nav(`/jobcards?open=${b.job_id}`); return; }
    try {
      const { data } = await api.post(`/bookings/${b.id}/convert`);
      toast.success(`Job card ${data.job_number} created from ${b.booking_number}`);
      nav(`/jobcards?open=${data.job_id}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const exportPdf = async () => {
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      const res = await fetch(`${API}/bookings/export.pdf?${p.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "bookings_export.pdf"; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Calendar grid
  const year = monthCursor.getFullYear(); const month = monthCursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const bookingsForDay = (d) => {
    const iso = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    return list.filter(b => b.slot_date === iso && b.status !== "cancelled");
  };

  return (
    <div className="p-8 print:p-0">
      <div className="print:hidden">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><CalIcon className="w-3 h-3"/> Workshop Bookings</div>
            <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Bookings</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-border rounded-sm overflow-hidden">
              <button data-testid="view-list" onClick={() => setView("list")} className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1 ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
                <List className="w-3 h-3"/> List
              </button>
              <button data-testid="view-calendar" onClick={() => setView("calendar")} className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1 ${view === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
                <LayoutGrid className="w-3 h-3"/> Calendar
              </button>
            </div>
            {canManage && (
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(emptyForm); setEditId(null); } }}>
                <DialogTrigger asChild>
                  <Button data-testid="add-booking-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                    <Plus className="w-4 h-4 mr-1"/> New Booking
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto" onKeyDownCapture={handleSimpleFormNav}>
                  <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Booking</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v, vehicle_id: "" })}>
                          <SelectTrigger data-testid="bk-customer" className="rounded-sm bg-secondary border-border flex-1"><SelectValue placeholder="Select customer"/></SelectTrigger>
                          <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button type="button" data-testid="add-cust-inline" onClick={() => setCustOpen(true)} variant="outline" className="rounded-sm border-primary/40 text-primary hover:bg-primary/10 shrink-0"><Plus className="w-4 h-4"/></Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={f.vehicle_id} onValueChange={(v) => setF({ ...f, vehicle_id: v })}>
                          <SelectTrigger data-testid="bk-vehicle" className="rounded-sm bg-secondary border-border flex-1"><SelectValue placeholder={f.customer_id ? "Select vehicle" : "Pick customer first"}/></SelectTrigger>
                          <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration} · {v.make} {v.model}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button type="button" data-testid="add-vehicle-inline" onClick={() => f.customer_id ? setVehOpen(true) : toast.error("Pick a customer first")} variant="outline" className="rounded-sm border-primary/40 text-primary hover:bg-primary/10 shrink-0" title="Add new vehicle"><Plus className="w-4 h-4"/></Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Job Type</Label>
                        <Select value={f.job_type} onValueChange={(v) => setF({ ...f, job_type: v })}>
                          <SelectTrigger data-testid="bk-job-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select job type"/></SelectTrigger>
                          <SelectContent>
                            {jobCardTypes.map(jt => <SelectItem key={jt.id} data-testid={`bk-jobtype-opt-${jt.name}`} value={jt.name}>{jt.name}</SelectItem>)}
                            {f.job_type && !jobCardTypes.some(jt => jt.name === f.job_type) && (
                              <SelectItem value={f.job_type}>{f.job_type}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Odometer Reading (km)</Label>
                        <Input data-testid="bk-odometer" type="number" min="0" value={f.odometer} onChange={(e) => setF(x => ({ ...x, odometer: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. 12500"/>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Package (optional)</Label>
                      <Select value={f.package_id} onValueChange={(v) => setF({ ...f, package_id: v })}>
                        <SelectTrigger data-testid="bk-package" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="None"/></SelectTrigger>
                        <SelectContent>{packages.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label>
                        <Input data-testid="bk-date" type="date" value={f.slot_date} onChange={(e) => setF(x => ({ ...x, slot_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Time</Label>
                        <Input data-testid="bk-time" type="time" value={f.slot_time} onChange={(e) => setF(x => ({ ...x, slot_time: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mechanic</Label>
                        <Select value={f.mechanic_id} onValueChange={(v) => setF({ ...f, mechanic_id: v })}>
                          <SelectTrigger data-testid="bk-mechanic" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Any"/></SelectTrigger>
                          <SelectContent>{mechanics.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Complaints / Notes</Label>
                      <Textarea data-testid="bk-notes" value={f.notes} onChange={(e) => setF(x => ({ ...x, notes: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={3}/>
                    </div>
                    {f.vehicle_id && (
                      <div data-testid="bk-service-history" className="border border-border rounded-sm overflow-hidden">
                        <div className="bg-secondary px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center justify-between">
                          <span>Previous Service History</span>
                          <span className="font-mono">{vehHist.length} visit{vehHist.length === 1 ? "" : "s"}</span>
                        </div>
                        {vehHist.length === 0 ? (
                          <div className="p-3 text-xs text-muted-foreground italic">No previous services recorded for this vehicle</div>
                        ) : (
                          <div className="max-h-52 overflow-y-auto overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-card text-[9px] uppercase tracking-widest text-muted-foreground sticky top-0">
                                <tr>
                                  <th className="text-left p-2">Job Card #</th>
                                  <th className="text-left p-2">Date</th>
                                  <th className="text-right p-2">Odometer</th>
                                  <th className="text-left p-2">Job Type</th>
                                  <th className="text-left p-2">Work Done</th>
                                  <th className="text-left p-2">Parts Used</th>
                                  <th className="text-right p-2">Invoice</th>
                                </tr>
                              </thead>
                              <tbody>
                                {vehHist.map((h, i) => (
                                  <tr key={i} data-testid={`bk-hist-row-${i}`} className="border-t border-border">
                                    <td className="p-2 font-mono">
                                      {h.job_id ? (
                                        <Link data-testid={`bk-hist-jobnum-${i}`} to={`/jobcards?open=${h.job_id}`} target="_blank" rel="noreferrer" className="text-primary hover:underline" title="Open full job card">{h.job_number}</Link>
                                      ) : (h.job_number || "-")}
                                    </td>
                                    <td className="p-2 font-mono">{h.date}</td>
                                    <td className="p-2 text-right font-mono">{h.odometer != null ? `${Number(h.odometer).toLocaleString("en-IN")} km` : "-"}</td>
                                    <td className="p-2">{h.job_type}</td>
                                    <td className="p-2 max-w-40 truncate" title={h.work_done}>{h.work_done}</td>
                                    <td className="p-2 max-w-36 truncate" title={h.parts_used}>{h.parts_used}</td>
                                    <td className="p-2 text-right font-mono">{h.invoice_amount != null ? inr(h.invoice_amount) : "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                    {!editId && (
                      <div className="text-xs text-muted-foreground border-t border-border pt-2">
                        On confirmation, a job card is auto-created and this booking is linked.
                      </div>
                    )}
                    <Button data-testid="save-booking-btn" onClick={submit} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">{editId ? "Update Booking" : "Create Booking"}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {view === "list" && (
          <>
            <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From Date</Label>
                <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To Date</Label>
                <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
              </div>
              <Button data-testid="filter-today" size="sm" variant="outline" onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }} className="rounded-sm text-xs uppercase tracking-wider">Today</Button>
              <Button data-testid="filter-all" size="sm" variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); }} className="rounded-sm text-xs uppercase tracking-wider">All Dates</Button>
              <div className="ml-auto flex items-center gap-2">
                <Button data-testid="export-pdf-bookings" size="sm" variant="outline" onClick={exportPdf} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> PDF</Button>
                <Button data-testid="print-bookings" size="sm" variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
              </div>
            </div>
            <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Booking #</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Slot</th>
                    <th className="text-left p-3">Vehicle</th>
                    <th className="text-left p-3">Job Type</th>
                    <th className="text-right p-3">Odometer</th>
                    <th className="text-left p-3">Mechanic</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No bookings for the selected date range</td></tr>}
                  {pg.pageItems.map(b => (
                    <tr key={b.id} data-testid={`booking-${b.id}`} className="border-t border-border hover:bg-secondary/50">
                      <td className="p-3 font-mono text-xs">
                        <button data-testid={`bknum-${b.id}`} onClick={() => openOrCreateJob(b)} className="text-primary hover:underline" title="Open / create job card">{b.booking_number}</button>
                      </td>
                      <td className="p-3">
                        <div>{b.customer_name || "-"}</div>
                        <a href={`tel:${b.customer_phone}`} className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3"/> {b.customer_phone || "-"}</a>
                      </td>
                      <td className="p-3 font-mono text-xs">{b.slot_date} · {b.slot_time}</td>
                      <td className="p-3 text-xs">{b.vehicle_label || "-"}</td>
                      <td className="p-3 text-xs">{b.job_type || b.service_type || b.package_name || "General"}{b.notes && <div className="text-muted-foreground italic mt-1">"{b.notes}"</div>}</td>
                      <td className="p-3 text-right font-mono text-xs">{b.odometer ? `${Number(b.odometer).toLocaleString("en-IN")} km` : "-"}</td>
                      <td className="p-3 text-xs">{b.mechanic_name || "-"}</td>
                      <td className="p-3"><span data-testid={`booking-status-${b.id}`} className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${statusColor[b.display_status] || statusColor.Pending}`}>{b.display_status || "Pending"}</span></td>
                      <td className="p-3 text-right space-x-1 whitespace-nowrap">
                        {b.status === "requested" && canManage && (
                          <>
                            <Button data-testid={`confirm-${b.id}`} size="sm" variant="ghost" onClick={() => setStatus(b.id, "confirmed")} className="h-7 text-xs text-blue-400"><CheckCircle2 className="w-3 h-3 mr-1"/> Confirm</Button>
                            <Button data-testid={`cancel-${b.id}`} size="sm" variant="ghost" onClick={() => setStatus(b.id, "cancelled")} className="h-7 text-xs text-destructive"><XCircle className="w-3 h-3 mr-1"/> Cancel</Button>
                          </>
                        )}
                        {b.status === "converted" && (
                          <Button data-testid={`open-job-${b.id}`} size="sm" variant="ghost" onClick={() => openJob(b)} className="h-7 text-xs text-primary"><Wrench className="w-3 h-3 mr-1"/> Open Job <ArrowRight className="w-3 h-3 ml-1"/></Button>
                        )}
                        {canManage && (
                          <Button data-testid={`edit-booking-${b.id}`} size="sm" variant="ghost" onClick={() => edit(b)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                        )}
                        {canDelete && (
                          <Button data-testid={`del-booking-${b.id}`} size="sm" variant="ghost" onClick={() => del(b)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationBar pg={pg}/>
            </div>
          </>
        )}

        {view === "calendar" && (
          <div className="border border-border bg-card rounded-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <Button size="sm" variant="ghost" onClick={() => setMonthCursor(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}><ChevronLeft className="w-4 h-4"/></Button>
              <div className="font-display font-black text-2xl uppercase tracking-tight">
                {monthCursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setMonthCursor(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}><ChevronRight className="w-4 h-4"/></Button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="text-center py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => (
                <div key={i} className={`min-h-24 border rounded-sm p-1 ${d ? "border-border bg-secondary/40" : "border-transparent"}`}>
                  {d && (
                    <>
                      <div className="text-[10px] text-muted-foreground font-mono">{d}</div>
                      {bookingsForDay(d).map(b => (
                        <button
                          key={b.id} data-testid={`cal-booking-${b.id}`}
                          onClick={() => b.status === "converted" ? openJob(b) : null}
                          className={`w-full text-left mt-1 px-1.5 py-1 rounded-sm border text-[10px] hover:opacity-80 ${rawStatusColor[b.status]}`}
                          title={`${b.slot_time} · ${b.customer_name}`}
                        >
                          <div className="font-mono">{b.slot_time}</div>
                          <div className="truncate">{b.customer_name}</div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Print sheet (hidden except when printing) */}
      <div className="hidden print:block print:bg-white print:text-black" id="print-bookings">
        <h1 className="text-xl font-bold mb-1">Workshop Bookings</h1>
        <div className="text-xs mb-3">Period: {dateFrom || "*"} to {dateTo || "*"} · Rows: {filtered.length}</div>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr>
              {["Booking #", "Date", "Customer", "Vehicle", "Job Type", "Odometer", "Mechanic", "Status"].map(h => (
                <th key={h} className="border border-black text-left p-1">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(b => (
              <tr key={b.id}>
                <td className="border border-black p-1">{b.booking_number}</td>
                <td className="border border-black p-1">{b.slot_date} {b.slot_time}</td>
                <td className="border border-black p-1">{b.customer_name || "-"}</td>
                <td className="border border-black p-1">{b.vehicle_label || "-"}</td>
                <td className="border border-black p-1">{b.job_type || b.service_type || b.package_name || "General"}</td>
                <td className="border border-black p-1">{b.odometer ? `${b.odometer} km` : "-"}</td>
                <td className="border border-black p-1">{b.mechanic_name || "-"}</td>
                <td className="border border-black p-1">{b.display_status || "Pending"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">New Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full Name</Label><Input data-testid="new-cust-name" value={newCust.name} onChange={(e) => setNewCust(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label><Input data-testid="new-cust-phone" value={newCust.phone} onChange={(e) => setNewCust(x => ({ ...x, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label><Textarea value={newCust.address} onChange={(e) => setNewCust(x => ({ ...x, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/></div>
            <Button data-testid="save-new-cust" onClick={saveCust} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save & Select</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={vehOpen} onOpenChange={setVehOpen}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">New Vehicle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Will be linked to <span className="text-primary font-semibold">{customers.find(c => c.id === f.customer_id)?.name || "the selected customer"}</span></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Make</Label><Input data-testid="new-veh-make" value={newVeh.make} onChange={(e) => setNewVeh(x => ({ ...x, make: e.target.value }))} placeholder="Honda" className="mt-1 rounded-sm bg-secondary border-border"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Model</Label><Input data-testid="new-veh-model" value={newVeh.model} onChange={(e) => setNewVeh(x => ({ ...x, model: e.target.value }))} placeholder="Activa 6G" className="mt-1 rounded-sm bg-secondary border-border"/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Registration No.</Label><Input data-testid="new-veh-reg" value={newVeh.registration} onChange={(e) => setNewVeh(x => ({ ...x, registration: e.target.value }))} placeholder="MH12 AB 1234" className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Year</Label><Input data-testid="new-veh-year" type="number" value={newVeh.year} onChange={(e) => setNewVeh(x => ({ ...x, year: e.target.value }))} placeholder="2023" className="mt-1 rounded-sm bg-secondary border-border"/></div>
            </div>
            <Button data-testid="save-new-veh" onClick={saveVeh} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save & Select</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
